import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAuthStore } from "@/stores/authStore";
import LoginForm from "@/components/auth/LoginForm";
import type { SocialAuthResult } from "@/services/socialAuth";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginError, isLoggingIn, socialLogin, isSocialLoggingIn, isAuthenticated, isAdmin, isController } = useAuth();
  const [googleError, setGoogleError] = useState<Error | null>(null);
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || "/";

  const getRedirectPath = (admin: boolean, controller: boolean) => {
    if (admin) return "/admin";
    if (controller) return "/controller";
    return from;
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate(getRedirectPath(isAdmin, isController), { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (data: { email: string; password: string }) => {
    await login(data);
    const { isAdmin: admin, isController: controller } = useAuthStore.getState();
    navigate(getRedirectPath(admin, controller), { replace: true });
  };

  const handleGoogleSuccess = async (result: SocialAuthResult) => {
    try {
      setGoogleError(null);
      await socialLogin({
        provider: result.provider,
        id_token: result.idToken,
        email: result.email,
        name: result.name,
        first_name: result.firstName,
        last_name: result.lastName,
      });
      const { isAdmin: admin, isController: controller } = useAuthStore.getState();
      navigate(getRedirectPath(admin, controller), { replace: true });
    } catch (error) {
      console.error("Google sign-in failed:", error);
      setGoogleError(error instanceof Error ? error : new Error("Google sign-in failed"));
    }
  };

  return (
    <div className="page-enter flex min-h-[calc(100vh-12rem)] items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary-600 text-white text-xl font-bold mx-auto">
            DT
          </div>
          <h1 className="mt-4 text-2xl font-bold text-gray-900">Welcome back</h1>
          <p className="mt-1 text-sm text-gray-500">Sign in to your DrivingTest UK account</p>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
          <LoginForm
            onSubmit={handleSubmit}
            onGoogleSuccess={handleGoogleSuccess}
            error={loginError || googleError}
            isLoading={isLoggingIn}
            isGoogleLoading={isSocialLoggingIn}
          />
        </div>
      </div>
    </div>
  );
}
